import { useMemo } from 'react';
import { Typography, Tooltip } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useTransactionTypeRender } from './useTransactionTypeRender';
import { usePaymentModeRender } from './usePaymentModeRender';
import { useUserTimestampRender } from '@src/hooks';
import dayjs from 'dayjs';

const { Text } = Typography;

export const useTransactionsColumns = (): SmartDataTableProps['columns'] => {
  const renderTransactionType = useTransactionTypeRender();
  const renderPaymentMode = usePaymentModeRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'transaction_type',
        title: 'Type',
        dataIndex: 'transaction_type',
        sorter: false,
        minWidth: 140,
        render: renderTransactionType,
      },
      {
        key: 'reference',
        title: 'Reference',
        dataIndex: 'transaction_type',
        sorter: false,
        minWidth: 120,
        render: (type: string, record: any) => {
          const referenceId = type === 'po' ? record.po_id : record.s_id;
          const prefix = type === 'po' ? 'PO-' : 'S-';

          // Create tooltip content based on transaction type
          const getTooltipContent = () => {
            if (type === 'po' && record.purchase_order_details) {
              const po = record.purchase_order_details;
              const supplier = po.supplier;
              const items = po.purchase_order_items || [];

              return (
                <div style={{ maxWidth: '400px' }}>
                  <div
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      fontSize: '14px',
                    }}
                  >
                    Purchase Order Details
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Order Date:</strong>{' '}
                    {po.order_date
                      ? new Date(po.order_date).toLocaleDateString()
                      : 'N/A'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Expected Delivery:</strong>{' '}
                    {po.expected_delivery_date
                      ? new Date(po.expected_delivery_date).toLocaleDateString()
                      : 'N/A'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Delivery Status:</strong>{' '}
                    {po.delivery_status || 'N/A'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Supplier:</strong> {supplier?.name || 'N/A'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Subtotal:</strong> ₹{po.subtotal || '0.00'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Tax:</strong> ₹{po.tax_amount || '0.00'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Total:</strong> ₹{po.total_amount || '0.00'}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Payment Status:</strong>{' '}
                    {po.payment_status || 'N/A'}
                  </div>
                  {items.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        Items:
                      </div>
                      {items.slice(0, 3).map((item: any, index: number) => (
                        <div
                          key={index}
                          style={{ fontSize: '12px', marginBottom: '2px' }}
                        >
                          • {item.medicine_details?.name || 'N/A'} - Qty:{' '}
                          {item.quantity} - ₹{item.net_amount}
                        </div>
                      ))}
                      {items.length > 3 && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          +{items.length - 3} more items
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            } else if (type === 'sell' && record.sale_details) {
              const sale = record.sale_details;
              const patient = sale.patient;
              const items = sale.sale_items || [];

              return (
                <div style={{ maxWidth: '400px' }}>
                  <div
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      fontSize: '14px',
                    }}
                  >
                    Sale Details
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Sale Date:</strong>{' '}
                    {sale.date
                      ? new Date(sale.date).toLocaleDateString()
                      : 'N/A'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Customer:</strong> {sale.customer_name || 'N/A'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Phone:</strong> {sale.customer_phone || 'N/A'}
                  </div>
                  {patient?.id && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Patient:</strong>{' '}
                      {patient.first_name && patient.last_name
                        ? `${patient.first_name} ${patient.last_name}`
                        : 'N/A'}
                    </div>
                  )}
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Subtotal:</strong> ₹{sale.subtotal || '0.00'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Discount:</strong> ₹
                    {sale.overall_discount_amount || '0.00'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Tax:</strong> ₹{sale.tax_amount || '0.00'}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Total:</strong> ₹{sale.total_amount || '0.00'}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Payment Status:</strong>{' '}
                    {sale.payment_status || 'N/A'}
                  </div>
                  {items.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        Items:
                      </div>
                      {items.slice(0, 3).map((item: any, index: number) => (
                        <div
                          key={index}
                          style={{ fontSize: '12px', marginBottom: '2px' }}
                        >
                          • {item.medicine?.name || 'N/A'} - Qty:{' '}
                          {item.quantity} - ₹{item.net_amount}
                        </div>
                      ))}
                      {items.length > 3 && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          +{items.length - 3} more items
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            return 'No details available';
          };

          return (
            <Tooltip
              title={getTooltipContent()}
              placement="topLeft"
              overlayStyle={{ maxWidth: '400px' }}
            >
              <Text
                style={{ fontWeight: 500, color: '#262626', cursor: 'pointer' }}
              >
                {referenceId ? `${prefix}${referenceId}` : 'N/A'}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        key: 'amount',
        title: 'Amount',
        dataIndex: 'amount',
        sorter: true,
        minWidth: 120,
        render: (amount: string) => (
          <Text style={{ fontWeight: 500, color: '#52c41a' }}>
            ₹
            {amount
              ? Number(amount).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                })
              : '0.00'}
          </Text>
        ),
      },
      {
        key: 'payment_mode',
        title: 'Payment Mode',
        dataIndex: 'payment_mode',
        sorter: false,
        minWidth: 130,
        render: renderPaymentMode,
      },

      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [renderTransactionType, renderPaymentMode, userTimestampRender]
  );
};
